/**
 * Market Intelligence dropdown — single nav entry that surfaces all three
 * data-driven stunts (My Real Cost, Area Prices, Real Price Index).
 *
 * Hover to open on desktop, tap to toggle on mobile. Click outside / press
 * Escape to close. Items prefetch their routes on hover so the click feels
 * instant.
 *
 * Used twice:
 *   - top nav (Navbar.jsx) — desktop variant
 *   - mobile menu (Navbar.jsx)        — accordion variant via the `mobile` prop
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, MapPin, Calculator, ChevronDown, BarChart3 } from 'lucide-react';
import { prefetchRoute } from '../utils/prefetch';
import { useMarket } from '../hooks/useMarket';

const buildItems = (market) => {
  const isUK = market.code === 'GB';
  const isUS = market.code === 'US';
  const postal = isUK ? 'postcode' : isUS ? 'ZIP' : 'Eircode';
  const register = isUK ? 'HM Land Registry' : isUS ? 'MLS/public records' : 'PPR';
  const marketAdj = isUK ? 'UK' : isUS ? 'US' : 'Irish';
  return [
    {
      to: market.withPrefix('/real-cost'),
      Icon: Calculator,
      title: 'My Real Cost',
      blurb: 'Stamp duty, fees, monthly outflow — the all-in number.',
      badge: 'NEW',
    },
    {
      to: market.withPrefix('/area-prices'),
      Icon: MapPin,
      title: 'Area Prices',
      blurb: `What did the neighbours actually pay? Per-${postal} ${register} snapshot.`,
    },
    {
      to: market.withPrefix('/price-index'),
      Icon: TrendingUp,
      title: 'Real Price Index',
      blurb: `The monthly ${marketAdj} house-price index, by ${isUK ? 'region' : isUS ? 'metro' : 'county'}.`,
    },
  ];
};

const MarketIntelligenceDropdown = ({ linkClass, transparent, mobile = false, onItemClick }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const market = useMarket();
  const ITEMS = buildItems(market);

  useEffect(() => {
    if (mobile) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [mobile]);

  // Mobile rendering: render every item inline, no dropdown chrome.
  if (mobile) {
    return (
      <div className="border-l-2 border-[#7042cc]/30 pl-3 my-1" data-testid="mobile-market-intel-section">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[#5b35a8] mb-1">
          Market Intelligence
        </div>
        {ITEMS.map(({ to, Icon, title }) => (
          <Link
            key={to}
            to={to}
            onClick={onItemClick}
            className="flex items-center gap-2 py-2 text-gray-700 hover:text-[#7042cc]"
            data-testid={`mobile-market-intel-${title.toLowerCase().replace(/ /g, '-')}`}
          >
            <Icon className="h-4 w-4" />
            <span>{title}</span>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="market-intel-dropdown-root"
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 ${linkClass}`}
        data-testid="market-intel-dropdown-trigger"
      >
        <BarChart3 className="h-3.5 w-3.5" />
        Market Intelligence
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 w-[20rem] rounded-xl border border-gray-200 bg-white shadow-xl py-2 z-[1000]"
          data-testid="market-intel-dropdown-panel"
        >
          {ITEMS.map(({ to, Icon, title, blurb, badge }) => (
            <Link
              key={to}
              to={to}
              onMouseEnter={() => prefetchRoute(to)}
              onClick={() => setOpen(false)}
              className="flex items-start gap-3 px-4 py-3 hover:bg-[#F5EFFF] transition-colors group"
              data-testid={`market-intel-item-${title.toLowerCase().replace(/ /g, '-')}`}
              role="menuitem"
            >
              <div className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#F5EFFF] text-[#5b35a8] group-hover:bg-[#7042cc] group-hover:text-white transition-colors">
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">{title}</span>
                  {badge && (
                    <span className="text-[9px] font-bold uppercase tracking-wider bg-[#7042cc] text-white px-1.5 py-0.5 rounded-sm">
                      {badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-0.5 leading-snug">{blurb}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketIntelligenceDropdown;
